<template>
    <App :title="pageTitle">
        <Block>
            <template #header>
                <h3 class="block-title">{{ pageTitle }}</h3>
            </template>

            <ul class="nav nav-tabs mb-4" role="tablist">
                <li class="nav-item" role="presentation">
                    <button class="nav-link active" id="assets-tab" data-bs-toggle="tab" data-bs-target="#assets-panel" type="button" role="tab">
                        <i class="fas fa-motorcycle me-2 text-muted"></i>Assets
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button class="nav-link" id="riders-tab" data-bs-toggle="tab" data-bs-target="#riders-panel" type="button" role="tab">
                        <i class="fas fa-users me-2 text-muted"></i>Riders
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button class="nav-link" id="transactions-tab" data-bs-toggle="tab" data-bs-target="#transactions-panel" type="button" role="tab">
                        <i class="fas fa-exchange-alt me-2 text-muted"></i>Transactions
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button class="nav-link" id="repayments-tab" data-bs-toggle="tab" data-bs-target="#repayments-panel" type="button" role="tab">
                        <i class="fas fa-file-invoice-dollar me-2 text-muted"></i>Credit Repayments
                    </button>
                </li>
            </ul>

            <div class="tab-content">
                <div class="tab-pane fade show active" id="assets-panel" role="tabpanel">
                    <Datatable :options="assetsOptions" />
                </div>
                <div class="tab-pane fade" id="riders-panel" role="tabpanel">
                    <Datatable :options="ridersOptions" />
                </div>
                <div class="tab-pane fade" id="transactions-panel" role="tabpanel">
                    <EnhancedTableComponent :api-endpoint="route('financier.ajax.table.transactions', { financier: financier })" :columns="transactionsColumns" title="Transactions" subtitle="Financier Transaction History" :enable-date-filter="true">
                        <template #row="{ item }">
                            <td class="px-4 py-3 text-muted small">{{ item.id }}</td>
                            <td class="px-4 py-3 text-muted small">{{ item.agent?.fullname || '—' }}</td>
                            <td class="px-4 py-3 text-muted small">{{ item.rider?.fullname || '—' }}</td>
                            <td class="px-4 py-3 text-muted small">{{ item.rider?.riderable?.title || '—' }}</td>
                            <td class="px-4 py-3 text-muted small">{{ formatMoney(item.amount) }}</td>
                            <td class="px-4 py-3 text-muted small text-break">{{ item.narration }}</td>
                            <td class="px-4 py-3 text-muted small">{{ formatDate(item.created_at) }}</td>
                            <td class="px-4 py-3 text-muted small text-center">
                                <div class="btn-group btn-group-sm" role="group">
                                    <a v-if="item.url?.single" class="btn btn-primary inertia-link" title="View Record" :href="item.url.single"><i class="fa fa-eye"></i></a>
                                </div>
                            </td>
                        </template>
                    </EnhancedTableComponent>
                </div>
                <div class="tab-pane fade" id="repayments-panel" role="tabpanel">
                    <EnhancedTableComponent :api-endpoint="route('financier.ajax.table.repayments', { financier: financier })" :columns="repaymentsColumns" title="Credit Repayments" subtitle="Financier Repayment Records" :enable-date-filter="true">
                        <template #row="{ item }">
                            <td class="px-4 py-3 text-muted small">{{ item.id }}</td>
                            <td class="px-4 py-3 text-muted small">{{ item.repayable?.rider?.fullname || '—' }}</td>
                            <td class="px-4 py-3 text-muted small">
                                {{ item.repayable_type?.includes('CreditRepair') ? 'Repair Credit' : 'Normal Credit' }}
                            </td>
                            <!-- <td class="px-4 py-3 text-muted small">
                                {{ item.paid_at ? `Sequence ${item.paid_sequence}` : `Sequence ${item.skipped_sequence}` }}
                            </td> -->
                            <td class="px-4 py-3 text-muted small">
                                {{ formatMoney(item.repayable?.credit_repayment_amount) }}
                            </td>
                            <td class="px-4 py-3 text-muted small">
                                <span class="badge" :class="item.paid_at ? 'bg-success' : 'bg-warning'">
                                    {{ item.paid_at ? 'Paid' : 'Skipped' }}
                                </span>
                            </td>
                            <td class="px-4 py-3 text-muted small">
                                {{ formatDate(item.paid_at || item.skipped_at) }}
                            </td>
                        </template>
                    </EnhancedTableComponent>
                </div>
            </div>
        </Block>
    </App>
</template>

<script lang="ts">
import Block from '@/vue/components/Block.vue'
import Datatable from '@/vue/components/Datatable.vue'
import EnhancedTableComponent from '@/vue/components/EnhancedTableComponent.vue'
import App from '@/vue/templates/App.vue'
import { CellRender, Column } from '@/vue/utils/Datatable'
import { Component, Prop, toNative, Vue } from 'vue-facing-decorator'

@Component({
    components: {
        App,
        Block,
        Datatable,
        EnhancedTableComponent,
    },
})
class Asset extends Vue {
    get buttons() {
        let buttons: any[] = []
        buttons.push('refresh')
        buttons.push('trashed')

        return buttons
    }

    @Prop({ type: Object })
    readonly financier!: any

    get pageTitle() {
        return 'Assets - ' + (this.financier?.title ?? '')
    }

    formatMoney(amount: any) {
        if (!amount) return '—'
        return CellRender.money()(amount)
    }

    formatDate(date: any) {
        if (!date) return '—'
        return CellRender.date()(date)
    }

    get assetsColumns() {
        let columns: any[] = []

        columns.push({
            data: 'serial',
            title: 'Serial Number',
        })

        columns.push({
            data: 'rider.fullname',
            title: 'Associated Rider',
            orderable: true,
            name: 'rider.fullname',
        })

        columns.push({
            data: 'allocation_status',
            title: 'Status',
            orderable: true,
            name: 'status',
        })

        columns.push({
            data: 'created_at',
            title: 'Registered',
            render: CellRender.date(),
            orderable: true,
            name: 'created_at',
        })

        columns.push(Column.actions(this.$gate, { view: false }))

        return columns
    }

    get ridersColumns() {
        let columns: any[] = []

        columns.push({
            data: 'id',
            title: 'ID',
            orderable: true,
            name: 'id',
        })

        columns.push({
            data: 'Name',
            title: 'Full Name',
            name: 'fullname',
            render: (data: string, _type: any, row: any) => {
                const label = row.financier ? 'Financier' : 'Swap'
                const color = row.financier ? 'secondary' : 'success'
                return `${data}<br><span class="badge bg-${color} mt-1">${label}</span>`
            },
        })

        columns.push({
            data: 'engine.serial',
            title: 'Assigned Engine',
            name: 'engine.serial',
            orderable: true,
            defaultContent: '—',
        })

        columns.push({
            data: 'riderable.title',
            title: 'Assigned Station/Cluster',
            defaultContent: '—',
        })

        columns.push({
            data: 'claims_count',
            title: 'No. of Claims',
            orderable: true,
            name: 'claims_count',
        })

        columns.push({
            data: 'stat',
            title: 'status',
            orderable: true,
            name: 'status',
        })

        columns.push({
            data: 'phone',
            title: 'Phone',
            name: 'phone',
        })

        columns.push({
            data: 'created_at',
            title: 'Registered',
            render: CellRender.date(),
            orderable: true,
            name: 'created_at',
        })

        columns.push(Column.actions(this.$gate))

        return columns
    }

    get transactionsColumns() {
        return [
            { key: 'id', label: 'Id', sortable: true },
            { key: 'agent.fullname', label: 'Agent Name' },
            { key: 'rider.fullname', label: 'Rider Name' },
            { key: 'rider.riderable.title', label: 'Rider Station/Cluster' },
            { key: 'amount', label: 'Amount' },
            { key: 'narration', label: 'Narration', sortable: true },
            { key: 'created_at', label: 'Created', sortable: true },
        ]
    }

    get repaymentsColumns() {
        return [
            { key: 'id', label: 'ID', sortable: true },
            { key: 'repayable.rider.fullname', label: 'Rider Name' },
            { key: 'repayable_type', label: 'Credit Type' },
            /* { key: 'paid_sequence', label: 'Sequence #' }, */
            { key: 'repayable.credit_repayment_amount', label: 'Amount' },
            { key: 'paid_at', label: 'Status' },
            { key: 'created_at', label: 'Repayment Date', sortable: true },
        ]
    }

    get assetsOptions() {
        return {
            ajax: {
                url: this.route('financier.ajax.table.assets', { financier: this.financier }),
                method: 'GET',
            },
            buttons: this.buttons,
            columns: this.assetsColumns,
        }
    }

    get ridersOptions() {
        return {
            ajax: {
                url: this.route('financier.ajax.table.riders', { financier: this.financier }),
                method: 'GET',
            },
            buttons: this.buttons,
            columns: this.ridersColumns,
        }
    }
}
export default toNative(Asset)
</script>
